import React, {
  memo,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  alpha,
  Chip,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import HotelIcon from '@mui/icons-material/Hotel';
import PaymentsIcon from '@mui/icons-material/Payments';
import HubIcon from '@mui/icons-material/Hub';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LanguageIcon from '@mui/icons-material/Language';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DashboardHeader from '../common/DashboardHeader';
import { isMobileShellLayout } from '../../utils/mobileShellLayout';
import { THEME_COLORS } from '../../shared/constants/theme';
import {
  userGuideModules,
  userGuideMeta,
  getGuide,
  getModule,
  getModuleGuides,
  getAdjacentGuides,
  searchUserGuide,
  guideOrder,
} from '../../data/userGuide';
import GuideBlocks from './userGuide/GuideBlocks';

const ACCENT = THEME_COLORS.PRIMARY;
const SIDEBAR_W = 280;
const TOC_W = 220;
const SIDEBAR_COLLAPSE_MS = 280;

const iconMap = {
  PlayArrow: PlayArrowIcon,
  CalendarToday: CalendarTodayIcon,
  People: PeopleIcon,
  BarChart: BarChartIcon,
  Settings: SettingsIcon,
  Dashboard: DashboardIcon,
  Business: BusinessIcon,
  CleaningServices: CleaningServicesIcon,
  Restaurant: RestaurantIcon,
  Inventory2: Inventory2Icon,
  Hotel: HotelIcon,
  Payments: PaymentsIcon,
  Hub: HubIcon,
  TrendingUp: TrendingUpIcon,
  Language: LanguageIcon,
  MenuBook: MenuBookIcon,
};

function readDocParam(search = window.location.search) {
  try {
    return new URLSearchParams(search).get('doc') || '';
  } catch {
    return '';
  }
}

/**
 * Roomnexa User Guide — docs shell with sticky nav, search, TOC, scroll-spy.
 */
const LandingUserGuide = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mobileShell = isMobileShellLayout();

  const activeDoc = readDocParam(location.search);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedModules, setExpandedModules] = useState(() =>
    Object.fromEntries(userGuideModules.map((m) => [m.id, false]))
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const articleRef = useRef(null);
  const searchRef = useRef(null);
  const homeSearchRef = useRef(null);
  const scrollParentsRef = useRef(null);

  const guide = activeDoc ? getGuide(activeDoc) : null;
  const moduleMeta = guide ? getModule(guide.moduleId) : null;
  const { prev, next } = guide ? getAdjacentGuides(guide.id) : { prev: null, next: null };

  const searchResults = useMemo(
    () => (searchQuery.trim() ? searchUserGuide(searchQuery) : []),
    [searchQuery]
  );

  // Keep only the active module expanded in the sidebar (faster initial paint).
  useEffect(() => {
    if (!guide?.moduleId) return;
    setExpandedModules((prev) => {
      if (prev[guide.moduleId]) return prev;
      return { ...prev, [guide.moduleId]: true };
    });
  }, [guide?.moduleId]);

  const collectScrollParents = useCallback(() => {
    const el = articleRef.current;
    const parents = [];
    if (el) parents.push(el);
    let parent = el?.parentElement || null;
    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      const oy = style.overflowY;
      if (
        (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
        parent.scrollHeight > parent.clientHeight + 1
      ) {
        parents.push(parent);
      }
      parent = parent.parentElement;
    }
    scrollParentsRef.current = parents;
    return parents;
  }, []);

  const scrollContentToTop = useCallback(() => {
    const parents = scrollParentsRef.current?.length
      ? scrollParentsRef.current
      : collectScrollParents();
    parents.forEach((node) => {
      node.scrollTop = 0;
    });
    window.scrollTo(0, 0);
  }, [collectScrollParents]);

  const openDoc = useCallback(
    (id) => {
      const params = new URLSearchParams(location.search);
      params.set('view', 'user-guide');
      if (id) params.set('doc', id);
      else params.delete('doc');
      setSearchQuery('');
      setMobileNavOpen(false);
      startTransition(() => {
        navigate(`/onboarding/landing/?${params.toString()}`, { replace: false });
      });
    },
    [location.search, navigate]
  );

  const openHome = useCallback(() => {
    setMobileNavOpen(false);
    startTransition(() => {
      navigate('/onboarding/landing/?view=user-guide', { replace: false });
    });
  }, [navigate]);

  // Single instant scroll on guide change — avoids flicker from stacked smooth scrolls.
  useEffect(() => {
    collectScrollParents();
    scrollContentToTop();
  }, [activeDoc, collectScrollParents, scrollContentToTop]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        const target = (!activeDoc && homeSearchRef.current) || searchRef.current;
        target?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeDoc]);

  useEffect(() => {
    if (!guide) return undefined;
    const root = articleRef.current;
    if (!root) return undefined;

    const sectionEls = guide.sections
      .map((s) => root.querySelector(`#ug-${s.id}`))
      .filter(Boolean);

    let ticking = false;
    let lastSection = '';

    const updateSection = () => {
      ticking = false;
      const scrollTop = root.scrollTop;
      let current = guide.sections[0]?.id || '';
      sectionEls.forEach((el) => {
        if (el.offsetTop - 120 <= scrollTop) current = el.id.replace(/^ug-/, '');
      });
      if (current !== lastSection) {
        lastSection = current;
        setActiveSection(current);
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateSection);
    };

    root.addEventListener('scroll', onScroll, { passive: true });
    updateSection();
    return () => root.removeEventListener('scroll', onScroll);
  }, [guide]);

  const surface = isDark ? '#161922' : '#ffffff';
  const pageBg = isDark ? '#0E1117' : '#F3F5F8';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)';
  const muted = isDark ? 'rgba(255,255,255,0.58)' : 'rgba(15,23,42,0.55)';
  const text = isDark ? '#F4F6F8' : '#12151C';

  const breadcrumbs = guide
    ? [
        { label: 'Home', path: '/onboarding/landing/' },
        { label: 'User Guide', path: '/onboarding/landing/?view=user-guide' },
        { label: moduleMeta?.title || 'Module' },
        { label: guide.title },
      ]
    : [
        { label: 'Home', path: '/onboarding/landing/' },
        { label: 'User Guide' },
      ];

  const scrollToSection = (sectionId) => {
    const root = articleRef.current;
    const el = root?.querySelector(`#ug-${sectionId}`);
    if (el && root) {
      root.scrollTo({ top: el.offsetTop - 72, behavior: 'smooth' });
    }
  };

  const navContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 2, pb: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: text,
                fontSize: '1.05rem',
              }}
            >
              User Guide
            </Typography>
            <Typography variant="caption" sx={{ color: muted }}>
              {guideOrder.length} guides · live product features
            </Typography>
          </Box>
          {!isMobile && (
            <IconButton
              size="small"
              onClick={() => setSidebarCollapsed(true)}
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
              sx={{
                mt: -0.25,
                color: muted,
                border: `1px solid ${border}`,
                borderRadius: 1.5,
                '&:hover': {
                  color: text,
                  borderColor: alpha(ACCENT, 0.45),
                  bgcolor: alpha(ACCENT, 0.08),
                },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box sx={{ mt: 1.5, position: 'relative', zIndex: 20 }}>
          <GuideSearchField
            inputRef={searchRef}
            query={searchQuery}
            onQueryChange={setSearchQuery}
            results={searchResults}
            isDark={isDark}
            text={text}
            muted={muted}
            border={border}
            surface={surface}
            onOpenDoc={openDoc}
            size="small"
            placeholder="Search docs…"
            showShortcut
          />
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1,
          pb: 2,
          overscrollBehavior: 'contain',
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha(ACCENT, 0.45)} transparent`,
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: 999,
            bgcolor: alpha(ACCENT, 0.4),
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'transparent',
          },
        }}
      >
        <NavButton
          active={!guide}
          onClick={openHome}
          isDark={isDark}
          icon={<HomeOutlinedIcon sx={{ fontSize: 18 }} />}
          label="All modules"
        />
        {userGuideModules.map((mod) => {
          const Icon = iconMap[mod.icon] || MenuBookIcon;
          const open = expandedModules[mod.id];
          const guides = getModuleGuides(mod.id);
          return (
            <Box key={mod.id} sx={{ mt: 0.75 }}>
              <Box
                onClick={() =>
                  setExpandedModules((s) => ({ ...s, [mod.id]: !s[mod.id] }))
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.9,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  color: text,
                  '&:hover': { bgcolor: alpha(ACCENT, 0.08) },
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 1,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: alpha(mod.accent, 0.15),
                    color: mod.accent,
                  }}
                >
                  <Icon sx={{ fontSize: 16 }} />
                </Box>
                <Typography sx={{ flex: 1, fontWeight: 650, fontSize: '0.86rem' }}>
                  {mod.title}
                </Typography>
                {open ? (
                  <ExpandLessIcon sx={{ fontSize: 18, color: muted }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 18, color: muted }} />
                )}
              </Box>
              <Collapse in={open}>
                <Box sx={{ ml: 1.5, borderLeft: `1px solid ${border}`, pl: 1 }}>
                  {guides.map((g) => (
                    <NavButton
                      key={g.id}
                      active={guide?.id === g.id}
                      onClick={() => openDoc(g.id)}
                      isDark={isDark}
                      label={g.title}
                      dense
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: mobileShell ? '100%' : 'calc(100vh - 60px)',
        bgcolor: pageBg,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
        <DashboardHeader
          title="User Guide"
          breadcrumbs={breadcrumbs}
          forceDark={mobileShell}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          minHeight: 0,
          px: { xs: 0, md: 2 },
          pb: { xs: 2, md: 3 },
          gap: sidebarCollapsed || isMobile ? 0 : 2,
          transition: `gap ${SIDEBAR_COLLAPSE_MS}ms ease`,
        }}
      >
        {/* Desktop sidebar */}
        {!isMobile && (
          <Box
            sx={{
              width: sidebarCollapsed ? 0 : SIDEBAR_W,
              minWidth: sidebarCollapsed ? 0 : SIDEBAR_W,
              flexShrink: 0,
              borderRadius: 3,
              border: sidebarCollapsed ? 'none' : `1px solid ${border}`,
              bgcolor: surface,
              overflow: 'hidden',
              position: 'sticky',
              top: 16,
              alignSelf: 'flex-start',
              height: 'calc(100vh - 100px)',
              maxHeight: 'calc(100vh - 100px)',
              display: 'flex',
              flexDirection: 'column',
              opacity: sidebarCollapsed ? 0 : 1,
              pointerEvents: sidebarCollapsed ? 'none' : 'auto',
              transition: [
                `width ${SIDEBAR_COLLAPSE_MS}ms ease`,
                `min-width ${SIDEBAR_COLLAPSE_MS}ms ease`,
                `opacity ${SIDEBAR_COLLAPSE_MS * 0.7}ms ease`,
                `border-color ${SIDEBAR_COLLAPSE_MS}ms ease`,
              ].join(', '),
            }}
          >
            <Box
              sx={{
                width: SIDEBAR_W,
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {navContent}
            </Box>
          </Box>
        )}

        {/* Main pane */}
        <Box
          ref={articleRef}
          sx={{
            flex: 1,
            minWidth: 0,
            borderRadius: { xs: 0, md: 3 },
            border: { xs: 'none', md: `1px solid ${border}` },
            bgcolor: surface,
            overflow: 'auto',
            maxHeight: { md: 'calc(100vh - 100px)' },
            position: 'relative',
            transition: `max-width ${SIDEBAR_COLLAPSE_MS}ms ease`,
          }}
        >
          <DocsTabBar
            activeDoc={activeDoc}
            text={text}
            muted={muted}
            border={border}
            surface={surface}
            onOpenDoc={openDoc}
            onOpenHome={openHome}
            sidebarCollapsed={!isMobile && sidebarCollapsed}
            onExpandSidebar={() => setSidebarCollapsed(false)}
          />

          {isMobile && (
            <Box
              sx={{
                position: 'sticky',
                top: 52,
                zIndex: 5,
                display: 'flex',
                gap: 1,
                p: 1.5,
                borderBottom: `1px solid ${border}`,
                bgcolor: surface,
              }}
            >
              <Button
                startIcon={<MenuIcon />}
                onClick={() => setMobileNavOpen(true)}
                sx={{ color: text, textTransform: 'none' }}
              >
                Contents
              </Button>
              <Button
                startIcon={<SearchIcon />}
                onClick={() => {
                  setMobileNavOpen(true);
                  setTimeout(() => searchRef.current?.focus(), 120);
                }}
                sx={{ color: text, textTransform: 'none' }}
              >
                Search
              </Button>
            </Box>
          )}

          {!guide ? (
            <HomeView
              isDark={isDark}
              text={text}
              muted={muted}
              border={border}
              surface={surface}
              onOpenDoc={openDoc}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              searchResults={searchResults}
              searchRef={homeSearchRef}
            />
          ) : (
            <ArticleView
              guide={guide}
              moduleMeta={moduleMeta}
              isDark={isDark}
              text={text}
              muted={muted}
              border={border}
              prev={prev}
              next={next}
              onNavigate={openDoc}
              onBackModule={() => {
                /* stay on article; scroll top / related */
                openHome();
              }}
              onBackTop={() => {
                scrollContentToTop();
              }}
              activeSection={activeSection}
              onSectionClick={scrollToSection}
              showToc={!isMobile}
            />
          )}
        </Box>
      </Box>

      {/* Mobile nav drawer */}
      <Drawer
        anchor="left"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        PaperProps={{
          sx: {
            width: Math.min(SIDEBAR_W + 20, 340),
            bgcolor: surface,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, flexShrink: 0 }}>
          <IconButton onClick={() => setMobileNavOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{navContent}</Box>
      </Drawer>
    </Box>
  );
};

/**
 * Inline search input with dropdown results (no modal).
 */
function GuideSearchField({
  inputRef,
  query,
  onQueryChange,
  results,
  isDark,
  text,
  muted,
  border,
  surface,
  onOpenDoc,
  size = 'small',
  placeholder = userGuideMeta.searchPlaceholder,
  showShortcut = false,
  fullWidth = true,
  elevated = false,
}) {
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const popularGuides = useMemo(
    () =>
      ['welcome', 'first-login', 'property-setup', 'org-hotelier', 'booking-list', 'pos-dashboard']
        .map((id) => getGuide(id))
        .filter(Boolean)
        .slice(0, 5),
    []
  );

  const hasQuery = Boolean(query.trim());
  const flatResults = hasQuery ? results : popularGuides;
  const showDropdown = open;

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(flatResults.length - 1, 0)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && flatResults[activeIndex]) {
        e.preventDefault();
        onOpenDoc(flatResults[activeIndex].id);
        setOpen(false);
        onQueryChange('');
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flatResults, activeIndex, onOpenDoc, onQueryChange]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-result-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const selectGuide = (id) => {
    onOpenDoc(id);
    setOpen(false);
    onQueryChange('');
  };

  return (
    <Box ref={rootRef} sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        inputRef={inputRef}
        fullWidth={fullWidth}
        size={size}
        placeholder={placeholder}
        value={query}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setOpen(true);
          onQueryChange(e.target.value);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: size === 'small' ? 18 : 22, color: ACCENT }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {query ? (
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQueryChange('');
                  }}
                  sx={{ color: muted }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              ) : showShortcut ? (
                <Chip
                  label="⌘K"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    bgcolor: alpha(ACCENT, 0.12),
                    color: muted,
                  }}
                />
              ) : null}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: elevated
              ? isDark
                ? 'rgba(0,0,0,0.4)'
                : 'rgba(255,255,255,0.92)'
              : isDark
                ? 'rgba(255,255,255,0.04)'
                : 'rgba(0,0,0,0.03)',
            '& fieldset': { borderColor: border },
            '&.Mui-focused fieldset': { borderColor: ACCENT },
            ...(elevated
              ? {
                  py: 0.35,
                  transition: 'border-color .2s, box-shadow .2s',
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused': {
                    boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.2)}`,
                  },
                }
              : null),
          },
        }}
      />

      {showDropdown && (
        <Box
          ref={listRef}
          sx={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 30,
            maxHeight: 360,
            overflow: 'auto',
            borderRadius: 2,
            border: `1px solid ${border}`,
            bgcolor: surface,
            boxShadow: isDark
              ? '0 16px 40px rgba(0,0,0,0.45)'
              : '0 16px 40px rgba(15,23,42,0.14)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              px: 1.5,
              pt: 1.25,
              pb: 0.5,
              color: muted,
              fontWeight: 750,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            }}
          >
            {hasQuery
              ? `${results.length} result${results.length === 1 ? '' : 's'}`
              : 'Popular guides'}
          </Typography>

          {hasQuery && results.length === 0 && (
            <Typography sx={{ px: 1.5, py: 2, color: muted, fontSize: '0.88rem' }}>
              No guides matched. Try another keyword.
            </Typography>
          )}

          <List disablePadding sx={{ pb: 0.75 }}>
            {flatResults.map((r, idx) => {
              const mod = getModule(r.moduleId);
              const Icon = iconMap[mod?.icon] || MenuBookIcon;
              const active = idx === activeIndex;
              return (
                <ListItemButton
                  key={r.id}
                  data-result-index={idx}
                  selected={active}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => selectGuide(r.id)}
                  sx={{
                    mx: 0.75,
                    mb: 0.25,
                    borderRadius: 1.5,
                    py: 1,
                    '&.Mui-selected': {
                      bgcolor: alpha(ACCENT, isDark ? 0.14 : 0.1),
                    },
                    '&:hover': { bgcolor: alpha(ACCENT, 0.1) },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: alpha(mod?.accent || ACCENT, 0.16),
                        color: mod?.accent || ACCENT,
                      }}
                    >
                      <Icon sx={{ fontSize: 16 }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={r.title}
                    secondary={mod?.title || r.pathHint}
                    primaryTypographyProps={{
                      fontWeight: 700,
                      color: text,
                      fontSize: '0.86rem',
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{
                      color: muted,
                      fontSize: '0.72rem',
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );
}

const DocsTabBar = memo(function DocsTabBar({
  activeDoc,
  text,
  muted,
  border,
  surface,
  onOpenDoc,
  onOpenHome,
  sidebarCollapsed,
  onExpandSidebar,
}) {
  const scrollerRef = useRef(null);
  const activeTabRef = useRef(null);
  const homeTabRef = useRef(null);
  const dragRef = useRef({
    tracking: false,
    dragging: false,
    startX: 0,
    scrollLeft: 0,
    pointerId: null,
    suppressClick: false,
  });
  const [dragging, setDragging] = useState(false);
  const [edgeFade, setEdgeFade] = useState({ left: false, right: true });

  const activeGuide = activeDoc ? getGuide(activeDoc) : null;
  const activeModuleId = activeGuide?.moduleId || null;

  const tabs = useMemo(() => {
    const ids = activeModuleId
      ? getModuleGuides(activeModuleId).map((g) => g.id)
      : guideOrder.slice(0, 12);
    return ids
      .map((id) => getGuide(id))
      .filter(Boolean)
      .map((g) => ({
        id: g.id,
        label: g.title,
        moduleId: g.moduleId,
      }));
  }, [activeModuleId]);


  const updateEdgeFade = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    setEdgeFade({
      left: scrollLeft > 4,
      right: scrollLeft + clientWidth < scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    updateEdgeFade();
    scroller.addEventListener('scroll', updateEdgeFade, { passive: true });
    window.addEventListener('resize', updateEdgeFade);
    return () => {
      scroller.removeEventListener('scroll', updateEdgeFade);
      window.removeEventListener('resize', updateEdgeFade);
    };
  }, [updateEdgeFade, tabs.length]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    // Home: always pin tabs to the start so Home is fully visible.
    if (!activeDoc) {
      scroller.scrollTo({ left: 0, behavior: 'smooth' });
      requestAnimationFrame(updateEdgeFade);
      return;
    }

    const el = activeTabRef.current;
    if (!el) return;
    const elLeft = el.offsetLeft;
    const elRight = elLeft + el.offsetWidth;
    const viewLeft = scroller.scrollLeft;
    const viewRight = viewLeft + scroller.clientWidth;
    const pad = 40;
    if (elLeft - pad < viewLeft) {
      scroller.scrollTo({ left: Math.max(0, elLeft - pad), behavior: 'smooth' });
    } else if (elRight + pad > viewRight) {
      scroller.scrollTo({
        left: elRight + pad - scroller.clientWidth,
        behavior: 'smooth',
      });
    }
  }, [activeDoc, updateEdgeFade]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const onWheel = (e) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      if (scroller.scrollWidth <= scroller.clientWidth) return;
      e.preventDefault();
      scroller.scrollLeft += e.deltaY + e.deltaX;
    };

    scroller.addEventListener('wheel', onWheel, { passive: false });
    return () => scroller.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    dragRef.current = {
      tracking: true,
      dragging: false,
      startX: e.clientX,
      scrollLeft: scroller.scrollLeft,
      pointerId: e.pointerId,
      suppressClick: false,
    };
  };

  const onPointerMove = (e) => {
    const state = dragRef.current;
    if (!state.tracking) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const dx = e.clientX - state.startX;

    if (!state.dragging) {
      if (Math.abs(dx) < 8) return;
      state.dragging = true;
      state.suppressClick = true;
      setDragging(true);
      try {
        scroller.setPointerCapture?.(state.pointerId);
      } catch {
        /* ignore */
      }
    }

    scroller.scrollLeft = state.scrollLeft - dx;
  };

  const endDrag = (e) => {
    const state = dragRef.current;
    if (!state.tracking) return;
    state.tracking = false;
    state.dragging = false;
    setDragging(false);
    try {
      scrollerRef.current?.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const handleTabClick = (id, isHome) => (e) => {
    e.stopPropagation();
    if (dragRef.current.suppressClick) {
      dragRef.current.suppressClick = false;
      return;
    }
    if (isHome) onOpenHome();
    else onOpenDoc(id);
  };

  const tabSx = (active, accent) => ({
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: 220,
    gap: 0.6,
    px: 1.35,
    py: 0.7,
    borderRadius: 999,
    border: `1px solid ${active ? alpha(accent || ACCENT, 0.55) : border}`,
    bgcolor: active ? ACCENT : 'transparent',
    color: active ? '#04140f' : muted,
    fontWeight: active ? 750 : 600,
    fontSize: '0.78rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 1,
    transition: 'background-color .15s, border-color .15s, color .15s',
    '&:hover': {
      borderColor: alpha(accent || ACCENT, 0.5),
      color: active ? '#04140f' : text,
      bgcolor: active ? ACCENT : alpha(ACCENT, 0.1),
    },
  });

  const maskImage = (() => {
    if (edgeFade.left && edgeFade.right) {
      return 'linear-gradient(90deg, transparent, #000 20px, #000 calc(100% - 20px), transparent)';
    }
    if (edgeFade.left) {
      return 'linear-gradient(90deg, transparent, #000 20px, #000 100%)';
    }
    if (edgeFade.right) {
      return 'linear-gradient(90deg, #000 0%, #000 calc(100% - 20px), transparent)';
    }
    return 'none';
  })();

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: { xs: 1, md: 1.25 },
        py: 1,
        borderBottom: `1px solid ${border}`,
        bgcolor: alpha(surface, 0.92),
        backdropFilter: 'blur(10px)',
      }}
    >
      {sidebarCollapsed && (
        <IconButton
          size="small"
          onClick={onExpandSidebar}
          aria-label="Expand sidebar"
          title="Expand sidebar"
          sx={{
            flexShrink: 0,
            color: muted,
            border: `1px solid ${border}`,
            borderRadius: 1.5,
            '&:hover': {
              color: text,
              borderColor: alpha(ACCENT, 0.45),
              bgcolor: alpha(ACCENT, 0.08),
            },
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}

      <IconButton
        size="small"
        aria-label="Scroll tabs left"
        onClick={() => {
          scrollerRef.current?.scrollBy({ left: -220, behavior: 'smooth' });
        }}
        sx={{
          display: { xs: 'none', sm: 'inline-flex' },
          flexShrink: 0,
          color: muted,
          opacity: edgeFade.left ? 1 : 0.35,
          '&:hover': { color: text, bgcolor: alpha(ACCENT, 0.08) },
        }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      <Box
        ref={scrollerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollBehavior: 'smooth',
          cursor: dragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          px: 0.25,
          '&::-webkit-scrollbar': { display: 'none' },
          maskImage,
          WebkitMaskImage: maskImage,
        }}
      >
        <Box
          ref={homeTabRef}
          component="button"
          type="button"
          data-doc-tab="home"
          onClick={handleTabClick(null, true)}
          onPointerDown={(e) => e.stopPropagation()}
          sx={tabSx(!activeDoc, ACCENT)}
        >
          <HomeOutlinedIcon sx={{ fontSize: 15 }} />
          Home
        </Box>

        {tabs.map((tab) => {
          const active = activeDoc === tab.id;
          const mod = getModule(tab.moduleId);
          return (
            <Box
              key={tab.id}
              ref={active ? activeTabRef : null}
              component="button"
              type="button"
              data-doc-tab={tab.id}
              title={tab.label}
              onClick={handleTabClick(tab.id, false)}
              onPointerDown={(e) => e.stopPropagation()}
              sx={tabSx(active, mod?.accent)}
            >
              {tab.label}
            </Box>
          );
        })}
      </Box>

      <IconButton
        size="small"
        aria-label="Scroll tabs right"
        onClick={() => {
          scrollerRef.current?.scrollBy({ left: 220, behavior: 'smooth' });
        }}
        sx={{
          display: { xs: 'none', sm: 'inline-flex' },
          flexShrink: 0,
          color: muted,
          opacity: edgeFade.right ? 1 : 0.35,
          '&:hover': { color: text, bgcolor: alpha(ACCENT, 0.08) },
        }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

function NavButton({ active, onClick, label, icon, dense, isDark }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: dense ? 1.25 : 1.25,
        py: dense ? 0.65 : 0.9,
        my: 0.15,
        borderRadius: 1.5,
        cursor: 'pointer',
        fontSize: dense ? '0.8rem' : '0.86rem',
        fontWeight: active ? 700 : 500,
        color: active ? (isDark ? '#04140f' : '#04140f') : isDark ? '#E8EAED' : '#1A1D23',
        bgcolor: active ? ACCENT : 'transparent',
        '&:hover': {
          bgcolor: active ? ACCENT : alpha(ACCENT, 0.1),
        },
      }}
    >
      {icon}
      <span>{label}</span>
    </Box>
  );
}

const HomeView = memo(function HomeView({
  isDark,
  text,
  muted,
  border,
  surface,
  onOpenDoc,
  searchQuery,
  onSearchQueryChange,
  searchResults,
  searchRef,
}) {
  const [expandedModules, setExpandedModules] = useState({});
  const PREVIEW_COUNT = 5;

  const toggleModuleGuides = (moduleId) => {
    setExpandedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  return (
    <Box>
      <Box
        sx={{
          px: { xs: 2.5, md: 5 },
          py: { xs: 3.5, md: 4.5 },
          background: isDark
            ? `radial-gradient(900px 320px at 8% -20%, ${alpha(ACCENT, 0.28)}, transparent 55%),
               linear-gradient(165deg, #141b24 0%, #0E1117 60%, #121820 100%)`
            : `radial-gradient(900px 300px at 0% 0%, ${alpha(ACCENT, 0.2)}, transparent 55%),
               linear-gradient(165deg, #F7FBFA 0%, #EEF2F6 100%)`,
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Chip
          label={`${guideOrder.length} guides · ${userGuideModules.length} modules`}
          size="small"
          sx={{
            mb: 1.75,
            fontWeight: 700,
            bgcolor: alpha(ACCENT, isDark ? 0.16 : 0.12),
            color: ACCENT,
            border: `1px solid ${alpha(ACCENT, 0.35)}`,
          }}
        />
        <Typography
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.04em',
            fontSize: { xs: '1.85rem', md: '2.45rem' },
            color: text,
            lineHeight: 1.1,
            maxWidth: 640,
          }}
        >
          {userGuideMeta.title}
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            color: muted,
            maxWidth: 560,
            fontSize: '1.05rem',
            lineHeight: 1.55,
          }}
        >
          {userGuideMeta.subtitle}
        </Typography>
        <Box sx={{ mt: 3, maxWidth: 560, position: 'relative', zIndex: 10 }}>
          <GuideSearchField
            inputRef={searchRef}
            query={searchQuery}
            onQueryChange={onSearchQueryChange}
            results={searchResults}
            isDark={isDark}
            text={text}
            muted={muted}
            border={border}
            surface={surface}
            onOpenDoc={onOpenDoc}
            size="medium"
            placeholder={userGuideMeta.searchPlaceholder}
            showShortcut
            elevated
          />
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, py: 3.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2.25,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 750, color: text, fontSize: '1.15rem' }}>
              Browse by module
            </Typography>
            <Typography variant="body2" sx={{ color: muted, mt: 0.35 }}>
              Open a guide below, or browse the full list inside each module.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              lg: '1fr 1fr 1fr',
            },
            gap: 2,
          }}
        >
          {userGuideModules.map((mod) => {
            const Icon = iconMap[mod.icon] || MenuBookIcon;
            const guides = getModuleGuides(mod.id);
            const expanded = Boolean(expandedModules[mod.id]);
            const canExpand = guides.length > PREVIEW_COUNT;
            const visibleGuides = expanded ? guides : guides.slice(0, PREVIEW_COUNT);

            return (
              <Box
                key={mod.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2.5,
                  border: `1px solid ${border}`,
                  background: isDark
                    ? `linear-gradient(180deg, ${alpha(mod.accent, 0.1)} 0%, rgba(255,255,255,0.03) 42%)`
                    : `linear-gradient(180deg, ${alpha(mod.accent, 0.08)} 0%, #ffffff 42%)`,
                  overflow: 'hidden',
                  transition: 'border-color .2s, transform .2s, box-shadow .2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    borderColor: alpha(mod.accent, 0.55),
                    boxShadow: isDark
                      ? `0 12px 28px ${alpha('#000', 0.35)}`
                      : `0 12px 28px ${alpha(mod.accent, 0.12)}`,
                  },
                }}
              >
                <Box
                  sx={{
                    height: 3,
                    background: `linear-gradient(90deg, ${mod.accent}, ${alpha(mod.accent, 0.25)})`,
                  }}
                />

                <Box sx={{ p: 2.25, pb: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1.35, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.75,
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                        bgcolor: alpha(mod.accent, isDark ? 0.22 : 0.14),
                        color: mod.accent,
                        border: `1px solid ${alpha(mod.accent, 0.35)}`,
                      }}
                    >
                      <Icon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 780,
                            color: text,
                            fontSize: '1.02rem',
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {mod.title}
                        </Typography>
                        <Chip
                          label={`${guides.length}`}
                          size="small"
                          sx={{
                            height: 20,
                            minWidth: 28,
                            fontSize: 11,
                            fontWeight: 750,
                            bgcolor: alpha(mod.accent, 0.16),
                            color: mod.accent,
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: muted,
                          mt: 0.55,
                          lineHeight: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {mod.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    mx: 2.25,
                    mb: 2.25,
                    mt: 0.25,
                    borderRadius: 2,
                    border: `1px solid ${border}`,
                    bgcolor: isDark ? 'rgba(0,0,0,0.28)' : 'rgba(255,255,255,0.75)',
                    overflow: 'hidden',
                  }}
                >
                  {visibleGuides.map((g, idx) => (
                    <Box
                      key={g.id}
                      component="button"
                      type="button"
                      onClick={() => onOpenDoc(g.id)}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.35,
                        py: 1.05,
                        border: 0,
                        borderBottom:
                          idx < visibleGuides.length - 1 || canExpand
                            ? `1px solid ${border}`
                            : 0,
                        background: 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                        color: text,
                        transition: 'background-color .15s',
                        '&:hover': {
                          bgcolor: alpha(mod.accent, isDark ? 0.14 : 0.1),
                          '& .guide-arrow': { color: mod.accent, transform: 'translateX(2px)' },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor: mod.accent,
                          opacity: 0.85,
                        }}
                      />
                      <Typography
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: '0.86rem',
                          fontWeight: 600,
                          color: text,
                          lineHeight: 1.35,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {g.title}
                      </Typography>
                      <ArrowForwardIcon
                        className="guide-arrow"
                        sx={{
                          fontSize: 15,
                          color: muted,
                          transition: 'color .15s, transform .15s',
                        }}
                      />
                    </Box>
                  ))}
                  {canExpand && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => toggleModuleGuides(mod.id)}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.35,
                        py: 0.95,
                        border: 0,
                        background: alpha(mod.accent, isDark ? 0.1 : 0.07),
                        cursor: 'pointer',
                        color: mod.accent,
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        '&:hover': { bgcolor: alpha(mod.accent, isDark ? 0.16 : 0.12) },
                      }}
                    >
                      {expanded
                        ? 'Show less'
                        : `View all ${guides.length} guides`}
                      {expanded ? (
                        <ExpandLessIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
});

const ArticleView = memo(function ArticleView({
  guide,
  moduleMeta,
  isDark,
  text,
  muted,
  border,
  prev,
  next,
  onNavigate,
  onBackModule,
  onBackTop,
  activeSection,
  onSectionClick,
  showToc,
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0, px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 } }}>
        <Chip
          label={moduleMeta?.title || guide.moduleId}
          size="small"
          sx={{
            mb: 1.5,
            bgcolor: alpha(moduleMeta?.accent || ACCENT, 0.14),
            color: moduleMeta?.accent || ACCENT,
            fontWeight: 700,
          }}
        />
        <Typography
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.035em',
            fontSize: { xs: '1.75rem', md: '2.15rem' },
            color: text,
            lineHeight: 1.15,
          }}
        >
          {guide.title}
        </Typography>
        <Typography sx={{ mt: 1.25, color: muted, fontSize: '1.05rem', maxWidth: 720 }}>
          {guide.summary}
        </Typography>
        {guide.pathHint && (
          <Typography variant="body2" sx={{ mt: 1, color: alpha(ACCENT, 0.95), fontWeight: 600 }}>
            Path: {guide.pathHint}
          </Typography>
        )}

        <Divider sx={{ my: 3, borderColor: border }} />

        {guide.sections.map((section) => (
          <Box key={section.id} id={`ug-${section.id}`} sx={{ mb: 4.5, scrollMarginTop: 72, contentVisibility: 'auto', containIntrinsicSize: '0 480px' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 750,
                color: text,
                mb: 2,
                letterSpacing: '-0.02em',
                pb: 1,
                borderBottom: `1px solid ${border}`,
              }}
            >
              {section.title}
            </Typography>
            <GuideBlocks blocks={section.blocks} isDark={isDark} onNavigate={onNavigate} />
          </Box>
        ))}

        {guide.related?.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 750, color: text, mb: 1.5 }}>
              Related Guides
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {guide.related.map((id) => {
                const g = getGuide(id);
                if (!g) return null;
                return (
                  <Chip
                    key={id}
                    label={g.title}
                    onClick={() => onNavigate(id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: alpha(ACCENT, 0.1),
                      border: `1px solid ${alpha(ACCENT, 0.3)}`,
                      fontWeight: 600,
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            pt: 2.5,
            pb: { xs: 10, md: 1 },
            borderTop: `1px solid ${border}`,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              disabled={!prev}
              onClick={() => prev && onNavigate(prev.id)}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                textTransform: 'none',
                color: text,
                border: `1px solid ${border}`,
                borderRadius: 2,
                px: { xs: 1, sm: 1.5 },
                py: 1.15,
                minHeight: { xs: 58, sm: 64 },
                minWidth: 0,
                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                '& .MuiButton-startIcon': {
                  mr: { xs: 0.75, sm: 1 },
                  flexShrink: 0,
                },
                '&:hover': {
                  bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.08),
                  borderColor: alpha(ACCENT, 0.45),
                },
                '&.Mui-disabled': {
                  borderColor: border,
                  opacity: 0.45,
                },
              }}
            >
              <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Typography
                  sx={{
                    display: 'block',
                    fontSize: '0.65rem',
                    fontWeight: 750,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: muted,
                    lineHeight: 1.2,
                    mb: 0.2,
                  }}
                >
                  Previous
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    color: text,
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {prev ? prev.title : 'No previous guide'}
                </Typography>
              </Box>
            </Button>

            <Button
              endIcon={<ArrowForwardIcon />}
              disabled={!next}
              onClick={() => next && onNavigate(next.id)}
              sx={{
                justifyContent: 'flex-end',
                textAlign: 'right',
                textTransform: 'none',
                color: text,
                border: `1px solid ${border}`,
                borderRadius: 2,
                px: { xs: 1, sm: 1.5 },
                py: 1.15,
                minHeight: { xs: 58, sm: 64 },
                minWidth: 0,
                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                '& .MuiButton-endIcon': {
                  ml: { xs: 0.75, sm: 1 },
                  flexShrink: 0,
                },
                '&:hover': {
                  bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.08),
                  borderColor: alpha(ACCENT, 0.45),
                },
                '&.Mui-disabled': {
                  borderColor: border,
                  opacity: 0.45,
                },
              }}
            >
              <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Typography
                  sx={{
                    display: 'block',
                    fontSize: '0.65rem',
                    fontWeight: 750,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: muted,
                    lineHeight: 1.2,
                    mb: 0.2,
                  }}
                >
                  Next
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    color: text,
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {next ? next.title : 'No next guide'}
                </Typography>
              </Box>
            </Button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Button
              onClick={onBackModule}
              sx={{
                flex: { xs: 1, sm: '0 0 auto' },
                textTransform: 'none',
                color: muted,
                border: `1px solid ${border}`,
                borderRadius: 2,
                px: 1.5,
                py: 1,
                minHeight: 44,
                '&:hover': {
                  color: text,
                  borderColor: alpha(ACCENT, 0.4),
                  bgcolor: alpha(ACCENT, 0.08),
                },
              }}
            >
              Back to modules
            </Button>
            <Button
              startIcon={<ArrowUpwardIcon />}
              onClick={onBackTop}
              sx={{
                flex: { xs: 1, sm: '0 0 auto' },
                textTransform: 'none',
                color: muted,
                border: `1px solid ${border}`,
                borderRadius: 2,
                px: 1.5,
                py: 1,
                minHeight: 44,
                '&:hover': {
                  color: text,
                  borderColor: alpha(ACCENT, 0.4),
                  bgcolor: alpha(ACCENT, 0.08),
                },
              }}
            >
              Top
            </Button>
          </Box>
        </Box>
      </Box>

      {showToc && (
        <Box
          sx={{
            width: TOC_W,
            flexShrink: 0,
            position: 'sticky',
            top: 56,
            alignSelf: 'flex-start',
            py: 4,
            pr: 2,
            pl: 1,
            borderLeft: `1px solid ${border}`,
            maxHeight: 'calc(100vh - 100px)',
            overflow: 'auto',
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: muted,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              px: 1,
            }}
          >
            On this page
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {guide.sections.map((s) => (
              <Box
                key={s.id}
                component="button"
                type="button"
                onClick={() => onSectionClick(s.id)}
                sx={{
                  textAlign: 'left',
                  border: 0,
                  background: 'none',
                  cursor: 'pointer',
                  px: 1,
                  py: 0.65,
                  borderLeft: `2px solid ${
                    activeSection === s.id ? ACCENT : 'transparent'
                  }`,
                  color: activeSection === s.id ? text : muted,
                  fontWeight: activeSection === s.id ? 700 : 500,
                  fontSize: '0.78rem',
                  lineHeight: 1.35,
                  '&:hover': { color: text },
                }}
              >
                {s.title}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default memo(LandingUserGuide);
